/**
 * @page BugReportDetail
 * @description Admin/Reporter-Detail-Page (#943, refactor #985).
 *   - 5+1 Status-Workflow: Eingegangen → In Arbeit → Review → Erledigt | Nicht behoben | Nicht reproduzierbar
 *   - Role-based Actions: Reporter sieht "Erledigt"+"Nicht behoben" nur bei status=review;
 *     Admin sieht zusaetzlich Status-Override + "Nicht reproduzierbar"
 *   - Nicht-behoben-Modal mit Pflicht-Begruendung (>=10 Zeichen) → Auto-Loop nach In Arbeit + iteration++
 *   - Audit-Verlauf (bug_status_history) als Timeline
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Bug, ExternalLink, ArrowLeft, MessageSquare, Send, RefreshCw, History, CheckCircle2, XCircle, AlertOctagon, Link2, Zap } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// Display-Status (5+1)
const STATUS_OPTIONS = [
  { key: 'eingegangen', labelKey: 'bugInbox.status.eingegangen' },
  { key: 'in_arbeit', labelKey: 'bugInbox.status.in_arbeit' },
  { key: 'review', labelKey: 'bugInbox.status.review' },
  { key: 'erledigt', labelKey: 'bugInbox.status.erledigt' },
  { key: 'nicht_reproduzierbar', labelKey: 'bugInbox.status.nicht_reproduzierbar' },
];

const STATUS_BADGE_COLORS = {
  eingegangen: 'bg-[var(--bg-inset)] border-line text-content-secondary',
  in_arbeit: 'bg-blue-50 border-blue-200 text-blue-700',
  review: 'bg-amber-50 border-amber-200 text-amber-700',
  erledigt: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  nicht_reproduzierbar: 'bg-[var(--bg-inset)] border-line text-content-secondary italic',
};

const STATUS_FALLBACK_MAP = {
  triage: 'eingegangen', an_claude: 'in_arbeit', deployed: 'review',
  erneut_kaputt: 'in_arbeit', abgeschlossen: 'erledigt',
};
const sKey = (s) => STATUS_FALLBACK_MAP[s] || s || 'eingegangen';

export default function BugReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [bug, setBug] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [fixCommit, setFixCommit] = useState('');
  const [showNichtBehobenModal, setShowNichtBehobenModal] = useState(false);
  const [showNichtReproModal, setShowNichtReproModal] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const [allowedTransitions, setAllowedTransitions] = useState([]);
  const [successToast, setSuccessToast] = useState(null);
  const [linkedPromptInput, setLinkedPromptInput] = useState('');
  const [linkedPromptUrl, setLinkedPromptUrl] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  function showSuccess(msg) {
    setSuccessToast(msg);
    setError(null);
    setTimeout(() => setSuccessToast(null), 3500);
  }

  async function loadBug() {
    const r = await fetch(`${API}/api/bug-reports/${id}`, { credentials: 'include' });
    const j = await r.json();
    if (j.bug) {
      setBug(j.bug);
      setFixCommit(j.bug.fix_commit || '');
      setAllowedTransitions(j.allowed_transitions || []);
      setLinkedPromptInput(j.bug.linked_prompt_page_id || '');
      setLinkedPromptUrl(j.bug.linked_prompt_url || '');
    } else { setError(j.error || 'not_found'); }
    setLoading(false);
  }
  async function loadMe() {
    try {
      const r = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      const j = await r.json();
      setMe(j.user || j);
    } catch { /* noop */ }
  }
  async function loadComments() {
    try {
      const r = await fetch(`${API}/api/bug-reports/${id}/notion-comments`, { credentials: 'include' });
      const j = await r.json();
      setComments(j.comments || []);
    } catch { /* noop */ }
  }
  async function loadHistory() {
    try {
      const r = await fetch(`${API}/api/bug-reports/${id}/history`, { credentials: 'include' });
      const j = await r.json();
      setHistory(j.history || []);
    } catch { /* noop */ }
  }

  useEffect(() => { loadBug(); loadMe(); loadComments(); loadHistory(); }, [id]);
  useEffect(() => {
    const iv = setInterval(() => { loadComments(); loadHistory(); }, 30000);
    return () => clearInterval(iv);
  }, [id]);

  async function setStatus(newStatus) {
    if (newStatus === 'nicht_reproduzierbar') { setShowNichtReproModal(true); return; }
    setSavingStatus(true);
    try {
      const r = await fetch(`${API}/api/bug-reports/${id}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, fix_commit: fixCommit.trim() || null }),
      });
      const j = await r.json();
      if (r.ok && j.bug) {
        setBug(j.bug);
        await loadBug();
        showSuccess(t('bugDetail.status_updated') || `Status: ${t(`bugInbox.status.${newStatus}`)}`);
      } else if (j.error === 'invalid_transition') {
        setError(`${t('bugDetail.invalid_transition') || 'Übergang nicht erlaubt'}: ${t(`bugInbox.status.${j.from}`)} → ${t(`bugInbox.status.${j.to}`)}`);
      } else {
        setError(j.error || `HTTP ${r.status}`);
      }
      await loadHistory();
    } catch (e) { setError(e.message); } finally { setSavingStatus(false); }
  }

  async function saveLinkedPrompt() {
    setSavingPrompt(true);
    try {
      const r = await fetch(`${API}/api/bug-reports/${id}/linked-prompt`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          linked_prompt_page_id: linkedPromptInput.trim() || null,
          linked_prompt_url: linkedPromptUrl.trim() || null,
        }),
      });
      const j = await r.json();
      if (r.ok && j.bug) {
        setBug(j.bug);
        showSuccess(t('bugDetail.linked_prompt_saved') || 'Verlinkter Prompt gespeichert');
      } else {
        setError(j.error || `HTTP ${r.status}`);
      }
    } catch (e) { setError(e.message); } finally { setSavingPrompt(false); }
  }

  async function triggerAudit() {
    setAuditing(true);
    setAuditResult(null);
    try {
      const r = await fetch(`${API}/api/bug-reports/${id}/audit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const j = await r.json();
      if (r.ok && j.ok) {
        setAuditResult({
          prompt_status: j.prompt_status,
          mapped_target: j.mapped_target,
          transitioned: j.transitioned,
        });
        if (j.bug) setBug(j.bug);
        await loadBug();
        await loadHistory();
        showSuccess(`Audit OK · Notion: ${j.prompt_status || '—'}${j.transitioned ? ` → ${t(`bugInbox.status.${j.mapped_target}`)}` : ''}`);
      } else {
        setError(j.error || `HTTP ${r.status}`);
      }
    } catch (e) { setError(e.message); } finally { setAuditing(false); }
  }

  async function bestaetigeErledigt() {
    setSavingStatus(true);
    try {
      const r = await fetch(`${API}/api/bug-reports/${id}/erledigt`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const j = await r.json();
      if (j.bug) setBug(j.bug);
      else setError(j.error || `HTTP ${r.status}`);
      await loadHistory();
    } catch (e) { setError(e.message); } finally { setSavingStatus(false); }
  }

  async function submitNichtBehoben() {
    if (reasonText.trim().length < 10) {
      setError(t('bugReport.kommentar_required') || 'Bitte mindestens 10 Zeichen Begruendung.');
      return;
    }
    setSavingStatus(true);
    try {
      const r = await fetch(`${API}/api/bug-reports/${id}/nicht-behoben`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reasonText.trim() }),
      });
      const j = await r.json();
      if (j.bug) {
        setBug(j.bug);
        setShowNichtBehobenModal(false);
        setReasonText('');
        await loadHistory();
        await loadComments();
      } else {
        setError(j.error || `HTTP ${r.status}`);
      }
    } catch (e) { setError(e.message); } finally { setSavingStatus(false); }
  }

  async function submitNichtRepro() {
    if (reasonText.trim().length < 10) {
      setError(t('bugReport.kommentar_required') || 'Bitte mindestens 10 Zeichen Begruendung.');
      return;
    }
    setSavingStatus(true);
    try {
      const r = await fetch(`${API}/api/bug-reports/${id}/status`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'nicht_reproduzierbar', reason: reasonText.trim() }),
      });
      const j = await r.json();
      if (j.bug) {
        setBug(j.bug);
        setShowNichtReproModal(false);
        setReasonText('');
        await loadHistory();
      } else {
        setError(j.error || `HTTP ${r.status}`);
      }
    } catch (e) { setError(e.message); } finally { setSavingStatus(false); }
  }

  async function postComment() {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/api/bug-reports/${id}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: commentText.trim() }),
      });
      setCommentText('');
      await loadComments();
    } catch (e) { setError(e.message); } finally { setSubmitting(false); }
  }

  if (loading) return <div className="p-6 text-sm text-content-secondary">{t('common.loading')}</div>;
  if (error && !bug) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-xs text-content-secondary hover:text-content mb-4 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> {t('common.back')}
        </button>
        <div className="bg-rose-50 border border-rose-200 rounded-card p-4 text-sm text-rose-700">{error}</div>
      </div>
    );
  }

  const myRole = me?.rolle;
  const isAdmin = ['admin', 'administrator', 'ops_manager'].includes(myRole);
  const isReporter = me?.id && bug.reporter_user_id === me.id;
  const currentStatus = sKey(bug.status);
  const inReview = currentStatus === 'review';

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <Link to="/admin/bug-reports" className="text-xs text-content-secondary hover:text-content inline-flex items-center gap-1">
          <ArrowLeft size={12} /> {t('bugInbox.title')}
        </Link>
        {bug.notion_url && (
          <a href={bug.notion_url} target="_blank" rel="noreferrer" className="text-xs text-[#338edc] hover:underline inline-flex items-center gap-1">
            {t('bugReport.open_notion')} <ExternalLink size={10} />
          </a>
        )}
      </div>

      <div className="bg-surface-card border border-line rounded-card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#338edc]/10 flex items-center justify-center flex-shrink-0">
            <Bug size={18} className="text-[#338edc]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-[var(--text-tertiary)]">{bug.br_nr || `BR-${bug.id}`}</span>
              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${STATUS_BADGE_COLORS[currentStatus] || STATUS_BADGE_COLORS.eingegangen}`}>
                {t(`bugInbox.status.${currentStatus}`)}
              </span>
              {bug.iteration > 1 && (
                <span className="px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-[10px] font-medium text-rose-700">
                  {t('bugReport.iteration')} {bug.iteration}
                </span>
              )}
            </div>
            <div className="text-base font-semibold text-content mt-1">{bug.bug_titel}</div>
            <div className="text-xs text-content-secondary mt-0.5">
              {bug.schweregrad}{bug.modul ? ` · ${bug.modul}` : ''}{bug.reporter_name ? ` · ${bug.reporter_name}` : ''}
            </div>
          </div>
        </div>

        {bug.was_passiert && (
          <Section label={t('bugReport.was_passiert_label')}>{bug.was_passiert}</Section>
        )}
        {bug.was_sollte && (
          <Section label={t('bugReport.was_sollte_label')}>{bug.was_sollte}</Section>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] font-mono text-[var(--text-tertiary)]">
          {bug.page_url && <div><span className="text-content-secondary">URL:</span> {bug.page_url}</div>}
          {bug.browser && <div><span className="text-content-secondary">Browser:</span> {bug.browser}</div>}
          {bug.viewport && <div><span className="text-content-secondary">Viewport:</span> {bug.viewport}</div>}
        </div>
        {bug.console_errors && (
          <Section label="Console-Errors">
            <pre className="text-[10px] font-mono whitespace-pre-wrap break-all max-h-40 overflow-y-auto rounded-lg border border-line bg-[var(--bg-inset)] p-2">{bug.console_errors}</pre>
          </Section>
        )}
      </div>

      {/* Action-Block: Reporter + Admin koennen bei status=review entscheiden */}
      {inReview && (isAdmin || isReporter) && (
        <div className="bg-surface-card border border-line rounded-card p-5 space-y-3">
          <div className="text-sm font-semibold text-content">{t('bugDetail.review_actions')}</div>
          <div className="text-xs text-content-secondary">{t('bugDetail.review_actions_hint')}</div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={bestaetigeErledigt}
              disabled={savingStatus}
              className="flex-1 min-w-[180px] px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <CheckCircle2 size={16} /> {t('bugDetail.action_erledigt')}
            </button>
            <button
              onClick={() => { setReasonText(''); setError(null); setShowNichtBehobenModal(true); }}
              disabled={savingStatus}
              className="flex-1 min-w-[180px] px-4 py-3 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <XCircle size={16} /> {t('bugDetail.action_nicht_behoben')}
            </button>
          </div>
        </div>
      )}

      {/* Admin-Status-Override */}
      {isAdmin && (
        <div className="bg-surface-card border border-line rounded-card p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-content inline-flex items-center gap-2">
              {t('bugDetail.admin_status')}
            </div>
            {bug.status_source === 'auto' && (
              <span className="text-[10px] text-content-secondary inline-flex items-center gap-1 font-mono">
                <Zap size={10} /> {t('bugDetail.status_source_auto') || 'auto-audit'}
              </span>
            )}
          </div>
          <div className="text-[11px] text-content-secondary">
            {t('bugDetail.allowed_transitions_hint') || 'Nur erlaubte Übergänge sind klickbar — siehe State-Machine.'}
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((s) => {
              const isCurrent = currentStatus === s.key;
              const isAllowed = isCurrent || allowedTransitions.includes(s.key);
              return (
                <button
                  key={s.key}
                  onClick={() => isAllowed && !isCurrent && setStatus(s.key)}
                  disabled={savingStatus || isCurrent || !isAllowed}
                  title={!isAllowed && !isCurrent ? (t('bugDetail.transition_blocked') || `Aus '${currentStatus}' nicht erlaubt`) : undefined}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                    isCurrent
                      ? 'border-[#338edc] bg-[#338edc]/10 text-content'
                      : isAllowed
                        ? 'border-line bg-surface-input text-content-secondary hover:bg-surface-hover'
                        : 'border-line bg-[var(--bg-inset)] text-[var(--text-tertiary)] cursor-not-allowed opacity-50'
                  }`}
                >
                  {t(s.labelKey)}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={fixCommit}
              onChange={(e) => setFixCommit(e.target.value)}
              placeholder={t('bugDetail.fix_commit_placeholder')}
              className="flex-1 px-3 py-2 rounded-lg border border-line bg-surface-input text-xs text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30"
            />
          </div>
        </div>
      )}

      {/* #1017 Auto-Audit: Verlinkter Notion-Prompt + Manuelles Audit */}
      {isAdmin && (
        <div className="bg-surface-card border border-line rounded-card p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-content inline-flex items-center gap-2">
              <Link2 size={14} /> {t('bugDetail.linked_prompt_title') || 'Verlinkter Notion-Prompt'}
            </div>
            {bug.auto_audit_at && (
              <span className="text-[10px] text-content-secondary font-mono">
                {t('bugDetail.last_audit') || 'Letztes Audit'}: {new Date(bug.auto_audit_at).toLocaleString('de-AT')} ({bug.auto_audit_status || '—'})
              </span>
            )}
          </div>
          <div className="text-[11px] text-content-secondary">
            {t('bugDetail.linked_prompt_hint') || 'Bei verlinktem Prompt synct der Auto-Audit-Cronjob (06:00) den Prompt-Status auf den Bug-Status: Bereit/WIP→In Arbeit, Deployed→Review, Bug→In Arbeit (Loop).'}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={linkedPromptInput}
              onChange={(e) => setLinkedPromptInput(e.target.value)}
              placeholder={t('bugDetail.linked_prompt_id_placeholder') || 'Notion-Page-ID (UUID, z.B. 3387ac45-…)'}
              className="px-3 py-2 rounded-lg border border-line bg-surface-input text-xs font-mono text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30"
            />
            <input
              type="text"
              value={linkedPromptUrl}
              onChange={(e) => setLinkedPromptUrl(e.target.value)}
              placeholder="https://www.notion.so/…"
              className="px-3 py-2 rounded-lg border border-line bg-surface-input text-xs text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={saveLinkedPrompt}
              disabled={savingPrompt}
              className="px-3 py-1.5 rounded-lg border border-line bg-surface-input text-xs text-content hover:bg-surface-hover disabled:opacity-50"
            >
              {savingPrompt ? '…' : (t('bugDetail.save_linked_prompt') || 'Verlinkung speichern')}
            </button>
            <button
              onClick={triggerAudit}
              disabled={auditing || !bug.linked_prompt_page_id}
              className="px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2a7bc4] disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Zap size={12} /> {auditing ? '…' : (t('bugDetail.audit_now') || 'Audit jetzt ausführen')}
            </button>
            {bug.linked_prompt_url && (
              <a href={bug.linked_prompt_url} target="_blank" rel="noreferrer" className="text-xs text-[#338edc] hover:underline inline-flex items-center gap-1 self-center">
                {t('bugDetail.open_prompt') || 'Prompt öffnen'} <ExternalLink size={10} />
              </a>
            )}
          </div>
          {bug.verifiziert_am && (
            <div className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 size={11} /> {t('bugDetail.verified_at') || 'Verifiziert'}: {new Date(bug.verifiziert_am).toLocaleString('de-AT')} ({bug.verifikation === 'gefixt' ? '✅ gefixt' : bug.verifikation === 'kaputt' ? '❌ kaputt' : '—'})
            </div>
          )}
        </div>
      )}

      {/* Audit-Log Timeline */}
      <div className="bg-surface-card border border-line rounded-card p-5 space-y-3">
        <div className="text-sm font-semibold text-content inline-flex items-center gap-2">
          <History size={14} /> {t('bugDetail.history_section')}
        </div>
        {history.length === 0 ? (
          <div className="text-xs text-content-secondary">{t('bugDetail.history_empty')}</div>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="rounded-lg border border-line bg-[var(--bg-inset)] p-3 text-xs">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${STATUS_BADGE_COLORS[sKey(h.from_status)] || ''}`}>
                      {h.from_status ? t(`bugInbox.status.${sKey(h.from_status)}`) : '—'}
                    </span>
                    <span className="text-content-secondary">→</span>
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${STATUS_BADGE_COLORS[sKey(h.to_status)] || ''}`}>
                      {t(`bugInbox.status.${sKey(h.to_status)}`)}
                    </span>
                    {h.to_iteration > h.from_iteration && (
                      <span className="text-[10px] text-rose-700">
                        · {t('bugReport.iteration')} {h.from_iteration} → {h.to_iteration}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-[var(--text-tertiary)]">
                    {new Date(h.created_at).toLocaleString('de-AT')}
                  </span>
                </div>
                <div className="text-[11px] text-content-secondary mt-1">
                  {h.user_name || (h.source === 'claude_code_api' ? 'Claude Code' : (h.source === 'notion_sync' ? 'Notion-Sync' : (h.source === 'system' ? 'System' : '—')))}
                  {h.source && h.source !== 'manual' && (
                    <span className="ml-1 text-[10px] text-[var(--text-tertiary)]">({h.source})</span>
                  )}
                </div>
                {h.reason && (
                  <div className="mt-1 text-content whitespace-pre-wrap">{h.reason}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-surface-card border border-line rounded-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-content inline-flex items-center gap-2">
            <MessageSquare size={14} /> {t('bugDetail.comments_section')}
          </div>
          <button
            onClick={loadComments}
            className="text-xs text-content-secondary hover:text-content inline-flex items-center gap-1"
          >
            <RefreshCw size={11} /> {t('common.refresh')}
          </button>
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {comments.length === 0 ? (
            <div className="text-xs text-content-secondary">{t('bugDetail.no_comments')}</div>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="rounded-lg border border-line bg-[var(--bg-inset)] p-3">
                <div className="text-xs text-content whitespace-pre-wrap">{c.text}</div>
                <div className="text-[10px] text-[var(--text-tertiary)] mt-1">
                  {new Date(c.created_time).toLocaleString('de-AT')}
                </div>
              </div>
            ))
          )}
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={2}
              placeholder={t('bugDetail.comment_placeholder')}
              className="flex-1 px-3 py-2 rounded-lg border border-line bg-surface-input text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 resize-none"
            />
            <button
              onClick={postComment}
              disabled={submitting || !commentText.trim()}
              className="px-3 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2a7bc4] transition-colors disabled:opacity-50 inline-flex items-center gap-1 self-end"
            >
              <Send size={12} /> {t('bugDetail.send_comment')}
            </button>
          </div>
        )}
      </div>

      {successToast && (
        <div className="fixed top-4 right-4 z-50 bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-xs text-emerald-700 shadow-md inline-flex items-center gap-2 max-w-md">
          <CheckCircle2 size={14} /> {successToast}
        </div>
      )}
      {error && bug && (
        <div className="bg-rose-50 border border-rose-200 rounded-card p-3 text-xs text-rose-700 inline-flex items-center justify-between gap-2">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700"><XCircle size={14} /></button>
        </div>
      )}

      {showNichtBehobenModal && (
        <ReasonModal
          title={t('bugDetail.nicht_behoben_title')}
          description={t('bugDetail.nicht_behoben_hint')}
          icon={<AlertOctagon size={20} className="text-rose-500" />}
          submitLabel={t('bugDetail.action_nicht_behoben')}
          submitting={savingStatus}
          value={reasonText}
          onChange={setReasonText}
          onCancel={() => { setShowNichtBehobenModal(false); setReasonText(''); }}
          onSubmit={submitNichtBehoben}
          t={t}
        />
      )}
      {showNichtReproModal && (
        <ReasonModal
          title={t('bugDetail.nicht_repro_title')}
          description={t('bugDetail.nicht_repro_hint')}
          icon={<AlertOctagon size={20} className="text-content-secondary" />}
          submitLabel={t('bugInbox.status.nicht_reproduzierbar')}
          submitting={savingStatus}
          value={reasonText}
          onChange={setReasonText}
          onCancel={() => { setShowNichtReproModal(false); setReasonText(''); }}
          onSubmit={submitNichtRepro}
          t={t}
        />
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-xs font-medium text-content-secondary mb-1">{label}</div>
      <div className="text-sm text-content whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function ReasonModal({ title, description, icon, submitLabel, submitting, value, onChange, onCancel, onSubmit, t }) {
  const minLen = 10;
  const ok = value.trim().length >= minLen;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="bg-surface-card border border-line rounded-card p-5 max-w-lg w-full space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          {icon}
          <div className="flex-1">
            <div className="text-sm font-semibold text-content">{title}</div>
            <div className="text-xs text-content-secondary mt-0.5">{description}</div>
          </div>
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={t('bugReport.kommentar_placeholder')}
          className="w-full px-3 py-2 rounded-lg border border-line bg-surface-input text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 resize-none"
        />
        <div className="flex items-center justify-between text-[11px]">
          <span className={ok ? 'text-emerald-600' : 'text-content-secondary'}>
            {value.trim().length}/{minLen} {t('bugDetail.min_chars')}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              disabled={submitting}
              className="px-3 py-1.5 rounded-lg border border-line bg-surface-input text-xs text-content-secondary hover:bg-surface-hover disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={onSubmit}
              disabled={submitting || !ok}
              className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-medium disabled:opacity-50"
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
